import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { BranchSelector } from "@/components/branch-selector";
import { PageContent } from "@/components/layout/page-content";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { TimePicker } from "@/components/ui/time-picker";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import { toast } from "@/lib/toast";

interface OpeningHoursSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/settings/hours")({
	component: OpeningHoursPage,
	validateSearch: (search: Record<string, unknown>): OpeningHoursSearch => ({
		branch: search.branch as string | undefined,
	}),
});

const daySchedule = z.object({
	enabled: z.boolean(),
	open: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/),
	close: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/),
});

const hoursSchema = z.object({
	monday: daySchedule,
	tuesday: daySchedule,
	wednesday: daySchedule,
	thursday: daySchedule,
	friday: daySchedule,
	saturday: daySchedule,
	sunday: daySchedule,
});

type HoursInput = z.infer<typeof hoursSchema>;

const DEFAULT_HOURS: HoursInput = {
	monday: { enabled: true, open: "09:00", close: "18:00" },
	tuesday: { enabled: true, open: "09:00", close: "18:00" },
	wednesday: { enabled: true, open: "09:00", close: "18:00" },
	thursday: { enabled: true, open: "09:00", close: "18:00" },
	friday: { enabled: true, open: "09:00", close: "18:00" },
	saturday: { enabled: false, open: "09:00", close: "18:00" },
	sunday: { enabled: false, open: "09:00", close: "18:00" },
};

function OpeningHoursPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/settings/hours",
	});

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const branchData =
		branchesData === undefined
			? undefined
			: (branchesData.find((b) => b._id === branchId) ?? null);

	const updateBranch = useMutation(api.features.branches.web.update);

	const {
		handleSubmit,
		control,
		watch,
		reset,
		formState: { isSubmitting, isDirty },
	} = useForm<HoursInput>({
		resolver: zodResolver(hoursSchema),
		defaultValues: DEFAULT_HOURS,
	});

	const watchedDays = watch();

	useEffect(() => {
		if (branchData) {
			reset(branchData.businessHours ?? DEFAULT_HOURS);
		}
	}, [branchData, reset]);

	const onSubmit = async (data: HoursInput) => {
		if (!orgId || !branchId) return;
		try {
			await updateBranch({
				orgId,
				branchId: branchId as Id<"branches">,
				businessHours: data,
			});
			toast.success(<>Opening hours saved</>);
		} catch (error) {
			handleConvexError(error);
		}
	};

	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({ to: ".", search: { branch: value } });
		},
		[navigate],
	);

	const controls = (
		<BranchSelector
			branches={branches}
			value={branchId as Id<"branches"> | undefined}
			onValueChange={handleBranchChange}
			placeholder="Select branch"
			className="w-48"
			onViewAll={() => navigate({ to: "/settings/branches" })}
			onCreateNew={() => navigate({ to: "/settings/branches" })}
		/>
	);

	if (!branchId && !isLoadingBranches) {
		return (
			<PageContent title="Opening Hours" controls={controls}>
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Create a branch to configure its opening hours."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={null}
					/>
				</div>
			</PageContent>
		);
	}

	if (branchData === undefined) {
		return (
			<PageContent title="Opening Hours" controls={controls}>
				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</PageContent>
		);
	}

	return (
		<PageContent title="Opening Hours" controls={controls}>
			<div className="space-y-6">
				<div className="flex flex-col gap-2">
					<h2 className="text-2xl font-bold">Opening Hours</h2>
					<p className="text-muted-foreground">
						Set when this branch is open each day of the week.
					</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					<Card className="p-4 sm:p-6">
						<div className="space-y-4">
							{(
								[
									{ key: "monday", label: "Monday" },
									{ key: "tuesday", label: "Tuesday" },
									{ key: "wednesday", label: "Wednesday" },
									{ key: "thursday", label: "Thursday" },
									{ key: "friday", label: "Friday" },
									{ key: "saturday", label: "Saturday" },
									{ key: "sunday", label: "Sunday" },
								] as const
							).map(({ key, label }) => {
								const enabled = watchedDays[key]?.enabled;
								return (
									<div
										key={key}
										className="grid grid-cols-[140px_1fr] items-center gap-4"
									>
										<div className="flex items-center gap-3">
											<Controller
												name={`${key}.enabled`}
												control={control}
												render={({ field }) => (
													<Switch
														checked={field.value}
														onCheckedChange={field.onChange}
													/>
												)}
											/>
											<span
												className={
													enabled
														? "font-medium"
														: "text-muted-foreground text-sm"
												}
											>
												{label}
											</span>
										</div>

										{enabled ? (
											<div className="flex items-center gap-2">
												<Controller
													name={`${key}.open`}
													control={control}
													render={({ field }) => (
														<TimePicker
															value={field.value}
															onChange={field.onChange}
															placeholder="09:00"
														/>
													)}
												/>
												<span className="text-muted-foreground text-sm">
													to
												</span>
												<Controller
													name={`${key}.close`}
													control={control}
													render={({ field }) => (
														<TimePicker
															value={field.value}
															onChange={field.onChange}
															placeholder="18:00"
														/>
													)}
												/>
											</div>
										) : (
											<span className="text-muted-foreground text-sm">
												Closed
											</span>
										)}
									</div>
								);
							})}
						</div>
					</Card>

					<div className="flex justify-end">
						<Button type="submit" disabled={!isDirty || isSubmitting}>
							{isSubmitting ? "Saving..." : "Save changes"}
						</Button>
					</div>
				</form>
			</div>
		</PageContent>
	);
}
